import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CorrectiveMaintenanceService } from 'app/modules/corrective-maintenance/services/corrective-maintenance.service';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { CMStatus } from 'app/modules/corrective-maintenance/model/corrective-maintenance.model';
import module from "app/modules/corrective-maintenance/corrective-maintenance.module";
import { EMPTY, map, merge, Subject, switchMap, takeUntil } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { CdftSearchStateService } from 'app/modules/cdft/state/search.state';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';

@Component({
  selector: 'app-cm-baseline',
  templateUrl: './cm-baseline.page.html',
  imports: module,
  standalone: true,
  styleUrls: ['./cm-baseline.page.scss']
})

export class CmBaselinePage {
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  @ViewChild(MatSort) private _sort: MatSort;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  searchForm: UntypedFormGroup;
  dt: any
  startingIndex: number = 0
  isLoading: boolean = false
  status = CMStatus;

  sortInfo: string = 'Created Date';
  private columnLabels: { [key: string]: string } = {
    ...DEFAULT_COLUMN_LABELS,
    source: 'Source',
    submissionYear: 'WPB Submission Year',
    failureDate: 'Failure Date',
    failEquipment: 'Failed Equipment',
    problemReported: 'Reported Problem',
    riskRanking: 'Risk Ranking',
    forwardActionPlan: 'Forward Action Plan',
    detailDesc: 'Description',
    rectifiedDate: 'Rectified Date',
    status: 'Status',
  };

  columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
    key,
    name,
    isChecked: false, // default unchecked
  }));

  constructor(
    private router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private cmServices: CorrectiveMaintenanceService,
    private state: CdftSearchStateService,) {
  }

  ngOnInit() {
    this.setSearch();
    this.setDataTable();
    this.setPage({ offset: 0 });
  }

  ngAfterViewInit() {
    if (this._sort && this._paginator) {
      // Set the initial sort
      this._sort.sort({
        id: 'field',
        start: 'asc',
        disableClear: true,
      });

      // Mark for check
      this._changeDetectorRef.markForCheck();

      // If the user changes the sort order...
      this._sort.sortChange
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          // Reset back to the first page
          this._paginator.pageIndex = 0;

          this.onSort(this._sort)
        });

      // Get platform types if sort or page changes
      merge(this._sort.sortChange, this._paginator.page).pipe(
        switchMap(() => {
          // this.isLoading = true;

          let pageInfo = {
            'count': this._paginator.length,
            'pageSize': this._paginator.pageSize,
            'limit': this._paginator.pageSize,
            'offset': this._paginator.pageIndex
          }
          this.setPage(pageInfo); // Emit page number

          return EMPTY
        }),
        map(() => {
          this.isLoading = false;
        }),
      ).subscribe();
    }
  }

  onSort(sort: any) {
    if (!sort) return;
    this.searchForm.patchValue({
      OrderBy1: sort.active,
      OrderDir: sort.direction,
    });

    this.sortInfo = this.columnLabels[sort.active] || sort.active;
    this.setPage({ offset: 0 });
  }

  handleSearch(event$) {
    const field = event$?.Field;
    const platform = event$?.Platform;
    const well = event$?.Well;
    const string = event$?.String;

    if ([field, platform, well, string].some(v => v != null && v !== '')) {
      this.setPage({ offset: 0 });
    }
  }

  setSearch() {
    this.sortInfo = 'Created Date'
    this.searchForm = this._formBuilder.group({
      Page: [0],
      PageSize: [20],
      OrderBy1: [null],
      OrderBy2: [null],
      OrderBy3: [null],
      OrderDir: 'asc',
      WellName: [''],
      PlatformName: [''],
      StringName: [''],
      FieldName: [''],
    });

    this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
  }

  setSize(size: number) {
    this.dt.page.size = size;
    this.dt.page.offset = 0
    this.setPage({ offset: this.dt.page.offset });
  }

  setPage(pageInfo) {
    const saved = this.state.get()
    let params: any = {}

    // if have saved search
    if (saved) {
      // is search for changes
      const useSaved = !this.searchForm.dirty;

      this.searchForm.patchValue(
        useSaved ? saved : { Page: pageInfo.offset + 1 },
        { emitEvent: !useSaved }
      );

      this.dt.page.offset = pageInfo.offset;
      this.startingIndex = this.dt.page.offset * (useSaved ? saved.PageSize : this.dt.page.size);
      params = useSaved ? saved : this.searchForm.value;

    } else {
      this.dt.page.offset = pageInfo.offset;
      this.searchForm.patchValue({ Page: this.dt.page.offset + 1 });
      this.startingIndex = this.dt.page.offset * this.dt.page.size;
      params = this.searchForm.value;
    }

    const search: any = {
      wellLongName: params.Well?.wellLongName,
      stringShortName: params.String?.stringShortName,
      platformLongName: params.Platform?.platformLongName,
      fieldLongName: params.Field?.fieldLongName,
      category: 'Baseline'
    };

    this.cmServices
      .getCMRecords({ ...params, search })
      .subscribe((pagedData) => {
      
        this.dt.page.totalPages = pagedData.data.totalPages;
        this.dt.page.totalRecords = pagedData.data.totalRecords;
        this.dt.rows = pagedData.data.data;
      });
  }

  setDataTable() {
    this.dt = {
      page: {
        size: this.searchForm.get('PageSize')?.value,
        totalRecords: 0,
        totalPages: 0,
        offset: 0,
      },
      rows: [],
    };
  }

  downloadCMRecords() {
    this.cmServices.downloadCMRecords('Baseline').subscribe((res) => {
      saveAs(res, 'Corrective Maintenence Records (Baseline)');
    })
  }

  handleFormatDate(dateString: any, withTime: boolean) {
    return handleFormatDate(dateString, withTime)
  }

  editCM(cmId) {
    if (this.searchForm.dirty) this.state.set(this.searchForm.value);
    this.router.navigate([`/data-management/corrective-maintenance/${cmId}/edit`]);
  }

  viewCM(cmId) {
    if (this.searchForm.dirty) this.state.set(this.searchForm.value);
    this.router.navigate([`/data-management/corrective-maintenance/${cmId}/view`]);
  }

  editWpb(cmRecord) {
    if (this.searchForm.dirty) this.state.set(this.searchForm.value);

    if (cmRecord.wpbId) {
      this.router.navigate([`/data-management/work-planning-budget/${cmRecord.wpbId}/edit`]);
    } else {
      this.router.navigate([`/data-management/work-planning-budget/${cmRecord.id}`]);
    }
  }

  reset() {
    this.state.clear(); // clear session storage
    this.setSearch();
    this.setPage({ offset: 0 });
  }

  applySort(payload: ApplySortPayload) {
    // Patch form (fill missing with null)
    const [OrderBy1, OrderBy2, OrderBy3] = [
      payload.columns[0]?.key ?? null,
      payload.columns[1]?.key ?? null,
      payload.columns[2]?.key ?? null,
    ];

    this.searchForm.patchValue({
      OrderBy1,
      OrderBy2,
      OrderBy3,
      OrderDir: payload.orderDir ?? 'asc',
    });

    const labels = payload.columns.map(k => k.name);
    this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
    this.setPage({ offset: 0 });
  }
}

